// voidInvoice.js
//
// Void an issued invoice: cancel open schedule rows; mark document void.
// Paid amounts → voidCreditPendingCents hook for Phase D project credit.

import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { updateNode } from 'services/nodes/NodeWriteService';
import { enqueueInvoiceQboSync } from 'services/quickbooks/QuickBooksService';
import {
  DOCUMENT_PAYMENT_TYPE_KEY,
} from 'nodetypes/documents/payments/documentPaymentKeys';
import {
  filterDocumentPayments,
  isDocumentPaymentOpenStatus,
  isDocumentPaymentPaidStatus,
  readDocumentPaymentField,
  sumPaidDocumentPayments,
} from 'nodetypes/documents/payments/documentPaymentsModel';
import { writeBackDocumentPaymentRollups } from 'nodetypes/documents/payments/writeBackDocumentPaymentRollups';
import { INVOICE_ITEM_TYPE_KEY, INVOICE_TYPE_KEY } from 'nodetypes/invoice/view/invoiceGeneralInfoModel';
import {
  isInvoiceIssued,
  isInvoiceVoided,
} from 'nodetypes/invoice/lifecycle/invoiceLifecycleModel';

function normalizeString(value) {
  return String(value || '').trim();
}

function mergeInvoiceFields(invoiceNode, fields) {
  let node = invoiceNode;
  const patch = {};
  Object.entries(fields).forEach(([fieldName, value]) => {
    if (value === undefined) return;
    const fieldPatch = buildNodeFieldPatch(
      { ...node, ...patch },
      {
        name: fieldName,
        source: 'invoice',
        type: fieldName === 'voidCreditPendingCents' ? 'money' : 'string',
      },
      INVOICE_TYPE_KEY,
      value,
    );
    if (!fieldPatch) return;
    Object.entries(fieldPatch).forEach(([sectionKey, sectionValue]) => {
      patch[sectionKey] = {
        ...(patch[sectionKey] || {}),
        ...sectionValue,
      };
      node = {
        ...node,
        [sectionKey]: {
          ...(node[sectionKey] || {}),
          ...sectionValue,
        },
      };
    });
  });
  return patch;
}

/**
 * @param {object} options
 * @param {object} options.invoiceNode
 * @param {object|null} [options.invoiceType]
 * @param {object[]} [options.neighborNodes]
 * @param {string} [options.reason]
 * @param {boolean} [options.notifyClient] - Stored for Send; no-op until email ships
 * @returns {Promise<{
 *   patch: object,
 *   canceledCount: number,
 *   pendingCreditCents: number,
 * }>}
 */
export async function voidInvoice({
  invoiceNode,
  invoiceType = null,
  neighborNodes = [],
  reason = '',
  notifyClient = false,
}) {
  if (!invoiceNode?.key) {
    throw new Error('Invoice is required to void.');
  }
  if (isInvoiceVoided(invoiceNode, invoiceType)) {
    throw new Error('This invoice is already voided.');
  }
  if (!isInvoiceIssued(invoiceNode, invoiceType)) {
    throw new Error('Issue the invoice before voiding it.');
  }

  const payments = filterDocumentPayments(neighborNodes);
  const items = (Array.isArray(neighborNodes) ? neighborNodes : [])
    .filter((row) => normalizeString(row?.type).toUpperCase() === INVOICE_ITEM_TYPE_KEY);

  let canceledCount = 0;
  for (const payment of payments) {
    const status = readDocumentPaymentField(payment, 'status');
    if (!isDocumentPaymentOpenStatus(status)) continue;
    const paymentPatch = buildNodeFieldPatch(
      payment,
      { name: 'status', source: 'document_payment', type: 'enum' },
      DOCUMENT_PAYMENT_TYPE_KEY,
      'canceled',
    );
    if (paymentPatch) {
      await updateNode(payment.key, paymentPatch);
      canceledCount += 1;
    }
  }

  const pendingCreditCents = sumPaidDocumentPayments(payments);
  const voidedAt = new Date().toISOString();
  const fields = {
    documentStatus: 'void',
    voidedAt,
    voidReason: normalizeString(reason),
    voidNotifyPending: notifyClient === true,
  };
  if (pendingCreditCents > 0) {
    fields.voidCreditPendingCents = pendingCreditCents;
  }

  const patch = mergeInvoiceFields(invoiceNode, fields);
  if (!Object.keys(patch).length) {
    throw new Error('Could not void invoice.');
  }
  await updateNode(invoiceNode.key, patch);

  // Refresh rollups against canceled schedule (paid rows remain for history).
  const refreshedPayments = payments.map((payment) => {
    const status = readDocumentPaymentField(payment, 'status');
    if (!isDocumentPaymentOpenStatus(status)) return payment;
    return {
      ...payment,
      data_document_payment: {
        ...(payment.data_document_payment || {}),
        status: 'canceled',
      },
    };
  });

  try {
    await writeBackDocumentPaymentRollups({
      parentNode: { ...invoiceNode, ...patch },
      parentType: invoiceType,
      items,
      payments: refreshedPayments.filter((payment) => (
        isDocumentPaymentPaidStatus(readDocumentPaymentField(payment, 'status'))
        || normalizeString(readDocumentPaymentField(payment, 'status')).toLowerCase() === 'canceled'
        || isDocumentPaymentOpenStatus(readDocumentPaymentField(payment, 'status'))
      )),
    });
  } catch (error) {
    console.warn('[voidInvoice] rollup write-back failed', error);
  }

  enqueueInvoiceQboSync({
    invoiceNode,
    reason: 'void',
  });

  return {
    patch,
    canceledCount,
    pendingCreditCents,
  };
}
